import { faSpinner } from '@awesome.me/kit-eee5f32034/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEchoPublic } from '@laravel/echo-react';
import { Form } from '@onwardtravelsolutions/package-react/Components';
import { NoticeType } from '@onwardtravelsolutions/package-react/Enums';
import {
    useControlledField,
    useControlledRecaptchaForm,
} from '@onwardtravelsolutions/package-react/Hooks';
import { generateUuid } from '@onwardtravelsolutions/package-react/Utilities/strings';
import { useEffect, useRef } from 'react';
import { ReCaptcha } from '../../Types';
import { SecondaryButton } from '../Buttons';
import { CheckboxesField, InputField, SelectField, TextField } from '../Fields';
import Heading from '../Heading';
import { NoticeList } from '../Notices';

const licences = [
    {
        label: 'Not Licenced Yet',
    },
    {
        label: 'Private Hire Operator',
    },
    {
        label: 'Hackney Carriage',
    },
    {
        label: 'Other',
    },
];

const sources = [
    {
        label: 'Please Select...',
        disabled: true,
    },
    {
        label: 'Word Of Mouth',
        disabled: false,
    },
    {
        label: 'From a Customer',
        disabled: false,
    },
    {
        label: 'From an Advert',
        disabled: false,
    },
    {
        label: 'Social Media',
        disabled: false,
    },
    {
        label: 'Search Engine',
        disabled: false,
    },
    {
        label: 'Other',
        disabled: false,
    },
];

const vehicles: string[] = [
    'Saloon Car',
    'Executive Car',
    'Estate Car',
    'People Carrier',
    'Executive People Carrier',
    '8 Seater Minibus',
];

type SignUpFormProps = {
    recaptcha: ReCaptcha;
    className?: string;
};

export default function SignUpForm({
    recaptcha,
    className = '',
}: SignUpFormProps) {
    const formRef = useRef<HTMLDivElement>(null);
    const isSubmittingRef = useRef<boolean>(false);
    const { controller, notices, submit, isSuccessful, isPending } =
        useControlledRecaptchaForm(
            'api/forms/drivers',
            recaptcha.siteKey,
            500,
            'post',
            {},
            'signup',
        );
    const nameControl = useControlledField(controller, 'company-name', {
        isRequired: true,
        index: 1,
    });
    const representativeControl = useControlledField(
        controller,
        'company-representative',
        { isRequired: true, index: 2 },
    );
    const postcodeControl = useControlledField(controller, 'company-postcode', {
        isRequired: true,
        index: 3,
    });
    const emailControl = useControlledField(controller, 'email', {
        isRequired: true,
        index: 4,
    });
    const telephoneControl = useControlledField(controller, 'telephone', {
        isRequired: true,
        index: 5,
    });
    const licenceControl = useControlledField(controller, 'licence', {
        isRequired: true,
        initialValue: 'Not Licenced Yet',
        index: 6,
    });
    const vehiclesControl = useControlledField(controller, 'vehicles', {
        isRequired: true,
        initialValue: [],
        index: 7,
    });
    const sourceControl = useControlledField(controller, 'source', {
        isRequired: true,
        initialValue: 'Please Select...',
        index: 8,
    });
    const bodyControl = useControlledField(controller, 'body', { index: 9 });

    /**
     * The following `useEcho` demonstrates the use of a Public Broadcasting
     * Channel, which can receive TestEvents from the server.
     */
    useEchoPublic('test', 'TestEvent', (event: any) =>
        console.log('TestEvent Received', event),
    );

    const formNotices = isSuccessful
        ? [
              {
                  title: 'Sign-Up Form Submitted',
                  body: 'We have received your sign-up enquiry and will be back in contact shortly with further instructions.',
                  type: NoticeType.Information,
                  uid: generateUuid(),
              },
          ]
        : notices;

    useEffect(() => {
        if (isSuccessful || notices.length) {
            // Scroll Form Into View
            formRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }
    }, [notices, isSuccessful]);

    return (
        <>
            <Form
                id='sign-up-form'
                className={`max-w-full [&>form]:divide-y [&>form]:divide-gray-300 ${className}`}
                onSubmit={async (event) => {
                    event.preventDefault();
                    if (isSubmittingRef.current) {
                        return;
                    }

                    isSubmittingRef.current = true;
                    await submit();
                    isSubmittingRef.current = false;
                }}
                isSubmitted={!!isSuccessful}
                notices={<NoticeList id='signup-form' notices={formNotices} />}
                ref={formRef}
            >
                <div className='space-y-6 pb-10'>
                    <Heading level={3}>General Information</Heading>
                    <div className='gap-6 grid grid-cols-1 md:grid-cols-3'>
                        <InputField
                            {...{
                                type: 'text',
                                id: 'company-name',
                                label: 'Company Name',
                                placeholder: 'Company Name / Trading Name',
                                ...nameControl,
                            }}
                        />

                        <InputField
                            {...{
                                type: 'text',
                                id: 'company-representative',
                                label: 'Your Name',
                                placeholder: 'Your Name / Representative Name',
                                ...representativeControl,
                            }}
                        />

                        <InputField
                            {...{
                                type: 'text',
                                id: 'company-postcode',
                                label: 'Company Postcode',
                                placeholder: 'Base Location Postcode',
                                ...postcodeControl,
                            }}
                        />
                    </div>
                </div>

                <div className='space-y-6 pb-10'>
                    <Heading level={3}>Contact Details</Heading>
                    <div className='gap-6 grid grid-cols-1 md:grid-cols-2'>
                        <InputField
                            {...{
                                type: 'email',
                                id: 'email',
                                label: 'Your Email',
                                placeholder: '...',
                                ...emailControl,
                            }}
                        />

                        <InputField
                            {...{
                                type: 'tel',
                                id: 'telephone',
                                label: 'Your Telephone',
                                placeholder: '...',
                                ...telephoneControl,
                            }}
                        />
                    </div>
                </div>

                <div className='space-y-6 pb-10'>
                    <Heading level={3}>Licence & Vehicle Details</Heading>

                    <div className='gap-6 grid grid-cols-1 md:grid-cols-2'>
                        <SelectField
                            {...{
                                id: 'licence',
                                label: 'Licence Type',
                                ...licenceControl,
                            }}
                        >
                            {licences.map((licence) => (
                                <option
                                    key={licence.label}
                                    value={licence.label}
                                >
                                    {licence.label}
                                </option>
                            ))}
                        </SelectField>

                        <div className='space-y-2'>
                            <h4 className='mt-0 mb-0 font-medium text-gray-600 leading-7'>
                                Fleet Details
                            </h4>
                            <div className='space-y-4'>
                                <CheckboxesField
                                    {...{
                                        id: 'vehicles',
                                        items: vehicles.map((vehicle) => ({
                                            value: vehicle,
                                            label: vehicle,
                                        })),
                                        ...vehiclesControl,
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className='space-y-6 border-b-0'>
                    <div className='prose'>
                        <Heading level={3}>Other Information</Heading>
                        <p className='font-light text-gray-600'>
                            Feel free to provide any additional information
                            about yourself or your company or ask any questions
                            you may have.
                        </p>
                    </div>
                    <div>
                        <SelectField
                            {...{
                                id: 'source',
                                label: 'How Did You Hear About Us?',
                                ...sourceControl,
                            }}
                        >
                            {sources.map((source) => (
                                <option
                                    key={source.label}
                                    value={source.label}
                                    disabled={source.disabled}
                                >
                                    {source.label}
                                </option>
                            ))}
                        </SelectField>

                        <TextField
                            {...{
                                id: 'body',
                                placeholder: '...',
                                className: 'block mt-4 w-full',
                                rows: 5,
                                ...bodyControl,
                            }}
                        />
                    </div>
                </div>

                <div className='block border-b-0'>
                    <SecondaryButton
                        type='submit'
                        className='w-full'
                        disabled={isPending}
                        tabIndex={10}
                    >
                        {isPending ? (
                            <FontAwesomeIcon
                                icon={faSpinner}
                                fontSize={22}
                                className='m-auto text-stone-950/50 animate-spin'
                            />
                        ) : (
                            'Submit Form'
                        )}
                    </SecondaryButton>
                </div>
            </Form>
        </>
    );
}
